/* eslint-disable max-lines */

import {
  duplicatePromptBlockMatcher,
  editLanguageMatcher,
  moveLanguageMatcher,
  promoteIdentifier,
  setCompareModeIdentifier,
  setCustomerGroupIdentifier,
  setPromptCompareMode,
} from 'actions/identifier';
import Modal from 'components/Modal';
import Labels from 'components/UsersPermissions/UserPermissionsForm/Labels';
import { MODAL_BACKGROUND } from 'constants/common';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getActiveLanguageMatcher,
  getActiveLanguageMatcherId,
  getChangedLanguageMatchersNames,
  getPromptBlockMove,
  getPromptCompareMode,
  getSelectedIdentifierRevision,
} from 'selectors/identifier';
import { useSelector } from 'store';
import DeleteMatcherButtonRedesign from '../DeleteMatcherButtonRedesign';
import MatcherTags from './MatcherTags';
import PromptBlockForm from './PromptBlockForm';

const PromptBlockMatcher: React.FC = () => {
  const dispatch = useDispatch();

  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);

  const identifierRevision = useSelector(getSelectedIdentifierRevision);

  const activeLanguageMatcher = useSelector(getActiveLanguageMatcher);
  const activeLanguageMatcherId = useSelector(getActiveLanguageMatcherId);
  const matcherMove = useSelector(getPromptBlockMove);

  const changedLanguageMatchers = useSelector(getChangedLanguageMatchersNames);
  const promptCompareMode = useSelector(getPromptCompareMode);

  useEffect(() => {
    if (activeLanguageMatcher && activeLanguageMatcher.isNew) {
      const input = document.getElementById('name');
      input?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLanguageMatcherId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const toggleModal = (): void => {
    setIsPromoteModalOpen((b) => !b);
  };

  const handlePromote = (): void => {
    if (identifierRevision?.identifier_head_uuid && identifierRevision.identifier_uuid) {
      dispatch(
        promoteIdentifier({
          identifierHead: identifierRevision.identifier_head_uuid,
          identifierId: identifierRevision.identifier_uuid,
        })
      );
    }
    toggleModal();
    dispatch(setCustomerGroupIdentifier('staging'));
    dispatch(setCompareModeIdentifier(false));
  };

  const handlePromptCompare = (): void => {
    dispatch(setPromptCompareMode({ isComparing: true, secondaryMatcher: '' }));
  };

  const handleDuplicateMatcher = (): void => {
    dispatch(duplicatePromptBlockMatcher({ matcherToDuplicate: activeLanguageMatcherId }));
  };

  const handleMove = (direction: 'up' | 'down'): void => {
    dispatch(moveLanguageMatcher({ languageMatcherId: activeLanguageMatcherId, direction }));
  };

  const handleSelectTag = (tag: string, add: boolean): void => {
    if (add) {
      dispatch(
        editLanguageMatcher({
          data: { tags: [...(activeLanguageMatcher?.tags || []), tag] },
          languageMatcherId: activeLanguageMatcherId,
        })
      );
    } else {
      dispatch(
        editLanguageMatcher({
          data: { tags: (activeLanguageMatcher?.tags || []).filter((t) => t !== tag) },
          languageMatcherId: activeLanguageMatcherId,
        })
      );
    }
  };

  const handleRemoveTag = ({ value }: { label: string; value: string }): void => {
    dispatch(
      editLanguageMatcher({
        data: { tags: (activeLanguageMatcher?.tags || []).filter((t) => t !== value) },
        languageMatcherId: activeLanguageMatcherId,
      })
    );
  };

  const renderLanguageMatcherForm = (): JSX.Element => (
    <div className="px-10 pt-4 pb-6 h-full flex flex-row justify-between gap-10">
      <div className="w-full flex flex-row gap-4">
        <PromptBlockForm promptCompare={promptCompareMode.isComparing} />
        {promptCompareMode.isComparing && (
          <PromptBlockForm promptCompare={promptCompareMode.isComparing} secondaryPrompt />
        )}
      </div>
      {!promptCompareMode.isComparing && (
        <div className="flex flex-col justify-between">
          <div className="flex flex-col gap-6 items-end">
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="w-44 h-8 button button--secondary justify-center"
                onClick={handlePromptCompare}
              >
                <span className="font-bold">Compare</span>
              </button>
              <button
                type="button"
                className="w-44 h-8 button button--secondary justify-center"
                onClick={handleDuplicateMatcher}
              >
                <span className="font-bold">Duplicate and Add</span>
              </button>
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                className="w-44 h-8 button button--secondary justify-center"
                onClick={(): void => {
                  handleMove('up');
                }}
                disabled={!matcherMove.up}
              >
                <span className="font-bold">Move Up</span>
              </button>
              <button
                type="button"
                className="w-44 h-8 button button--secondary justify-center"
                onClick={(): void => {
                  handleMove('down');
                }}
                disabled={!matcherMove.down}
              >
                <span className="font-bold">Move Down</span>
              </button>
            </div>
            <div className="w-50 flex flex-col gap-2">
              <div className="w-50 flex flex-col gap-1">
                <span>Tag</span>
                <MatcherTags
                  handleSelectTag={handleSelectTag}
                  tagsSelected={activeLanguageMatcher.tags || []}
                  allTags={activeLanguageMatcher.tags || []}
                />
              </div>
              <Labels
                handleDelete={handleRemoveTag}
                includedLabels={(activeLanguageMatcher.tags || []).map((t) => ({
                  label: t,
                  value: t,
                }))}
              />
            </div>
          </div>
          <div className="flex flex-row self-end">
            <DeleteMatcherButtonRedesign
              text="Remove Block"
              confirmationModalText="Are you sure you want to delete this prompt block?"
              setValue={(): void => {
                // moot
              }}
              setHighlightedLanguageMatcher={(): void => {
                // moot
              }}
              activeLanguageMatcher={activeLanguageMatcher}
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderBody = (): JSX.Element => (
    <>
      <div className="absolute top-5 left-0 right-0 h-40 z-0">{MODAL_BACKGROUND}</div>

      <div className="flex flex-col mt-30 mb-8 gap-4">
        <h2 className="text-heading-1">Push Changes?</h2>
        <div className="flex flex-col gap-2">
          <p className="text-body">Changes have been accepted for these matchers:</p>
          <ul className="list-disc list-outside pl-8 flex flex-col gap-1 text-body">
            {changedLanguageMatchers.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="w-full min-h-104 h-full flex flex-co justify-center overflow-auto custom-scrollbar">
        <div className={`flex flex-col flex-1 ${!activeLanguageMatcher && 'justify-center'}`}>
          {renderLanguageMatcherForm()}
        </div>
      </div>
      {isPromoteModalOpen && (
        <Modal
          title=" "
          body={renderBody()}
          okButton
          okButtonText="Push to Prod"
          okButtonOnClick={handlePromote}
          okButtonStyle="whitespace-no-wrap w-full px-4 mr-4"
          cancelButtonText="Go Back"
          cancelButtonOnclick={(): void => setIsPromoteModalOpen(false)}
          toggleShowModal={toggleModal}
        />
      )}
    </>
  );
};

export default PromptBlockMatcher;
